/**
 * @module UsePollingRefetch
 * @purpose Re-run a refetch callback on a fixed interval while `enabled` is true.
 *          Declarative counterpart to the imperative pollers (use-status-polling,
 *          use-board-wide-generate): the caller drives polling from derived state
 *          rather than an explicit start/stop call site.
 * @depends React useEffect, useRef
 *
 * @rules
 * - Lifecycle: no interval registered while `enabled` is false; the interval is
 *   torn down on unmount and whenever `enabled`/`intervalMs` change.
 * - Stability: the latest `refetch` is held in a ref so a changing callback
 *   identity does NOT restart the interval mid-poll.
 * - Overlap: an in-flight ref skips a tick while the previous refetch is still
 *   resolving (skip-if-busy — no queueing).
 * - Errors: transient refetch failures are swallowed; the underlying list hooks
 *   retain their last-good state.
 */

import { useEffect, useRef } from 'react'

const DEFAULT_POLL_INTERVAL_MS = 2500

/**
 * @behaviour While `enabled`, fires `refetch` every `intervalMs`, never overlapping.
 * @errors Never throws; swallows refetch rejections.
 */
export function usePollingRefetch(
  refetch: () => Promise<void> | void,
  enabled: boolean,
  intervalMs: number = DEFAULT_POLL_INTERVAL_MS
): void {
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (!enabled) return undefined

    const id = setInterval(() => {
      if (inFlightRef.current) return
      inFlightRef.current = true
      void (async () => {
        try {
          await refetchRef.current()
        } catch {
          /* keep polling on transient errors — list hooks retain last-good state */
        } finally {
          inFlightRef.current = false
        }
      })()
    }, intervalMs)

    return () => {
      clearInterval(id)
    }
  }, [enabled, intervalMs])
}
